import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import LatestList from "@/pages/home/components/LatestList";
import CollectionShowcase from "@/pages/home/components/CollectionShowcase";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import { LoadingState } from "@/components/base/StateViews";
import { getPopular, collections, films } from "@/lib/filmData";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";

const popular = getPopular(8);

export default function Home() {
  const loadingNews = useSimulatedLoading(["home-news"], 400);

  useDocumentMeta({
    title: "新金瓶梅影视资源与剧情介绍平台",
    description:
      "新金瓶梅为您提供最新的影视作品资讯、剧情介绍及相关影视资源，涵盖电影和电视剧内容，满足您的多样观看需求。",
    keywords: "新金瓶梅,新金瓶梅剧情,新金瓶梅影视,新金瓶梅电影,新金瓶梅电视剧,影视资源,剧情介绍",
  });

  const stats = [
    { label: "收录作品", value: films.length, icon: "ri-stack-line" },
    { label: "电影作品", value: films.filter((f) => f.type === "movies").length, icon: "ri-movie-2-line" },
    { label: "剧集作品", value: films.filter((f) => f.type === "tv").length, icon: "ri-tv-2-line" },
    { label: "热门专题", value: collections.length, icon: "ri-bookmark-3-line" },
  ];

  return (
    <div>
      <HeroCarousel />

      <section className="page-shell py-12 md:py-16">
        <SectionHeading
          title="按分类浏览"
          subtitle="电影与电视剧两大频道，快速定位你感兴趣的影视内容。"
        />
        <CategoryShortcuts />
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="page-shell">
          <SectionHeading
            title="最新影视资讯"
            subtitle="按发布时间倒序更新，第一时间掌握新片与热剧的剧情动态。"
            moreTo="/category/movies"
            moreLabel="查看更多"
          />
          {loadingNews ? <LoadingState label="正在加载最新影视资讯…" /> : <LatestList />}
        </div>
      </section>

      <section className="page-shell py-12 md:py-16">
        <SectionHeading
          title="热门影片推荐"
          subtitle="近期关注度最高的影视作品，剧情与口碑俱佳。"
          moreTo="/search"
          moreLabel="搜索更多"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {popular.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="page-shell">
          <SectionHeading
            title="热门推荐专题"
            subtitle="按主题精选的影视片单，帮你更快找到想看的故事。"
          />
          <CollectionShowcase />
        </div>
      </section>

      <section className="page-shell py-12 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-information-line" aria-hidden="true"></i>
              关于本站
            </span>
            <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              新金瓶梅，一站式影视资讯与剧情介绍平台
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们聚焦电影与电视剧的作品资讯、剧情介绍及相关资源展示，用清晰的信息层级与图文并茂的呈现方式，
              帮助影视爱好者快速了解一部作品讲什么、值不值得看。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              站内所有影视作品、演职人员与评分均为示例数据，用于展示内容结构与浏览体验，不涉及任何真实的播放或下载服务。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-team-line" aria-hidden="true"></i>
                了解我们
              </Link>
              <Link
                to="/search"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
                搜索影视
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {stats.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <p className="mt-4 font-heading text-2xl font-extrabold text-foreground-950">
                  {item.value}
                </p>
                <p className="mt-1 text-sm text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}